import { useState } from "react";
import { useTranslation } from "../../localization/useTranslation.ts";
import { InputFieldTypeEnum } from "./InputFields.types.ts";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types.ts";
import { getEnumValues } from "@common/enumOperations.ts";
import { setFirstLetterToLowerCase } from "@common/stringOperations.ts";
import { useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { InputFieldsProps } from "./InputFields.tsx";
import { TemplateEntityType } from "../EntityTemplate.types.ts";
import { exhaustiveGuard } from "@common/utilities.ts";
import { useFeatureFlags } from "../../auth/planProvider/PlanProvider.tsx";
import { FeatureFlag } from "../../auth/planProvider/PlanProvider.types.ts";

export function useInputFields(props: InputFieldsProps) {
    const queryClient = useDotLegalQueryClient();

    const { translateString } = useTranslation();

    const [selectedTab, setSelectedTab] = useState<number>(1);
    const [showCreateDialog, setShowCreateDialog] = useState(false);

    const onSaved = () => {
        setShowCreateDialog(false);
        queryClient.refetchQueriesByPredicate((query) => query.queryKey.some((key) => typeof key === "string" && key.includes("/settings/input-fields")));
    };

    return {
        breadCrumbs: getBreadCrumbs(),
        selectedTab,
        setSelectedTab,
        showCreateDialog,
        setShowCreateDialog,
        onSaved,
    };

    function getBreadCrumbs() {
        switch (props.templateEntityType) {
            case TemplateEntityType.Contract:
                return [{ name: translateString("contractInputFields") }];
            case TemplateEntityType.System:
                return [{ name: translateString("assetInputFields") }];
            case TemplateEntityType.LegalEntity:
                return [{ name: translateString("legalEntityInputFields") }];
            case TemplateEntityType.IncidentLog:
                return [{ name: translateString("incidentLogInputFields") }];
            case TemplateEntityType.Process:
                return [{ name: translateString("processInputFields") }];
            default:
                exhaustiveGuard(props.templateEntityType);
        }
    }
}

export function useCreateInputTypeFilterOptions(): Array<DotLegalSelectOption> {
    const { translateString } = useTranslation();
    const featureFlags = useFeatureFlags();
    const isDatePeriodFieldEnabled = featureFlags.isEnabled(FeatureFlag.CalculatedFieldFormulas);

    return getEnumValues(InputFieldTypeEnum)
        .filter((type) => isDatePeriodFieldEnabled || type !== InputFieldTypeEnum.DatePeriodField)
        .map((x) => {
            const name = translateString(setFirstLetterToLowerCase(InputFieldTypeEnum[x].toString()));
            return { name: name, id: x.toString() };
        });
}
